import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { firestore } from '../../../firebase';
import {
  ContainerType,
  getMaxCapacityCm,
  getMaxCapacityLiters,
  type TContainerType,
} from '../containerTypes';
import { getFlowTotalLiters } from 'general/flowVolume';

// Type for the latest sensor log
export interface LatestSensorLog {
  id: string;
  docId: string;
  EE: string;
  Te: string;
  cm: string;
  createTime: Date;
  l: string;
  m2?: string;
  m3?: string;
}

// Type for processed sensor data
export interface ProcessedSensorData {
  deviceId: string;
  levelCm: number;
  levelLiters: number;
  temperature: number;
  lastUpdated: Date;
  rawData: LatestSensorLog;
}

// API function to get the latest log for a device
const getLatestSensorLog = async (deviceId: string): Promise<LatestSensorLog | null> => {
  if (!deviceId) {
    throw new Error('Device ID is required');
  }

  const snap = await firestore
    .collection('flowDevices')
    .where('id', '==', deviceId)
    .orderBy('createTime', 'desc')
    .limit(1)
    .get();

  if (snap.empty) {
    return null;
  }

  const doc = snap.docs[0];
  return {
    docId: doc.id,
    ...doc.data(),
    createTime: doc.data().createTime.toDate(),
  } as LatestSensorLog;
};

// Process raw log data into usable format
const processSensorData = (log: LatestSensorLog): ProcessedSensorData => {
  return {
    deviceId: log.id,
    levelCm: parseFloat(log.cm) || 0,
    levelLiters: getFlowTotalLiters(log),
    temperature: parseFloat(log.Te) || 0,
    lastUpdated: log.createTime,
    rawData: log,
  };
};

// Custom hook for getting the latest sensor log
export const useLatestSensorLog = (deviceId: string | null) => {
  const query = useQuery({
    queryKey: ['latestSensorLog', deviceId],
    queryFn: async (): Promise<ProcessedSensorData | null> => {
      if (!deviceId) {
        return null;
      }
      const log = await getLatestSensorLog(deviceId);
      return log ? processSensorData(log) : null;
    },
    enabled: !!deviceId,
    staleTime: 30 * 1000, // 30 seconds (more frequent updates for real-time data)
    gcTime: 2 * 60 * 1000, // 2 minutes cache
    refetchInterval: 60 * 1000, // Refetch every minute for real-time updates
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Handle error notifications
  React.useEffect(() => {
    if (query.isError) {
      console.error('Error fetching latest sensor log:', query.error);
    }
  }, [query.isError, query.error]);

  return {
    ...query,
    sensorData: query.data,
    levelCm: query.data?.levelCm || 0,
    levelLiters: query.data?.levelLiters || 0,
    temperature: query.data?.temperature || 0,
    lastUpdated: query.data?.lastUpdated,
    isConnected: !!query.data && !query.isError,
  };
};

export { ContainerType, getMaxCapacityCm, getMaxCapacityLiters };
export type { TContainerType };

// Hook specifically for level percentage calculations
export const useLatestLevelPercentage = (
  deviceId: string | null,
  containerType: TContainerType,
  thresholdCm?: number
) => {
  const { sensorData, isLoading, error, isConnected } = useLatestSensorLog(deviceId);

  const maxCapacityCm = getMaxCapacityCm(containerType);
  const maxCapacityLiters = getMaxCapacityLiters(containerType);

  const levelPercent = React.useMemo(() => {
    if (!sensorData || maxCapacityCm <= 0) return 0;
    return Math.min(100, Math.max(0, (sensorData.levelCm / maxCapacityCm) * 100));
  }, [sensorData?.levelCm, maxCapacityCm]);

  const thresholdPercent = React.useMemo(() => {
    if (!thresholdCm || maxCapacityCm <= 0) return undefined;
    return Math.min(100, Math.max(0, (thresholdCm / maxCapacityCm) * 100));
  }, [thresholdCm, maxCapacityCm]);

  // Calculate actual liters based on level percentage and max capacity
  const levelLiters = React.useMemo(() => {
    if (!sensorData || maxCapacityLiters <= 0) return 0;
    return (levelPercent / 100) * maxCapacityLiters;
  }, [levelPercent, maxCapacityLiters]);

  // Calculate threshold liters based on threshold percentage
  const thresholdLiters = React.useMemo(() => {
    if (!thresholdPercent || maxCapacityLiters <= 0) return undefined;
    return (thresholdPercent / 100) * maxCapacityLiters;
  }, [thresholdPercent, maxCapacityLiters]);

  return {
    levelPercent: Math.round(levelPercent * 100) / 100, // Round to 2 decimal places
    thresholdPercent: thresholdPercent ? Math.round(thresholdPercent * 100) / 100 : undefined,
    levelCm: sensorData?.levelCm || 0,
    levelLiters: Math.round(levelLiters * 100) / 100, // Use calculated liters
    thresholdLiters: thresholdLiters ? Math.round(thresholdLiters * 100) / 100 : undefined,
    maxCapacityLiters,
    temperature: sensorData?.temperature || 0,
    lastUpdated: sensorData?.lastUpdated,
    maxCapacityCm,
    isLoading,
    error,
    isConnected,
  };
};
